import { authHeader } from "@/helpers/auth-header";
import { Config } from "@/config";
import axios from "axios";
import { Dataset } from "@/types/Dataset";
import { handleAxiosError } from "@/services/axiosErrorHandler";

function logout() {
  // remove user from local storage to log user out
  localStorage.removeItem("user");
}

// --------- Response handling -------------------------------

function handleResponse(response: Response) {
  return response.text().then(text => {
    const data = text && JSON.parse(text);
    if (!response.ok) {
      if (response.status === 401) {
        // auto logout if 401 response returned from api
        logout();
        //location.reload(true);
      }
      const error = (data && data.msg) || response.statusText;
      const additionalInformation = data.additionalInformation;

      return Promise.reject({ error, additionalInformation, data });
    }
    return data;
  });
}

function activateUser(code: string) {
  const requestOptions = {
    method: "GET"
  };

  return fetch(Config.apiUrl + "/confirm/" + code, requestOptions).then(
    handleResponse
  );
}

function listDatasets(): Promise<Array<Dataset>> {
  return axios({
    url: `${Config.apiUrl}/datasets`,
    method: "GET",
    responseType: "json",
    headers: authHeader()
  })
    .then(resp => Promise.resolve(resp.data))
    .catch(handleAxiosError);
}

function login(username: string, password: string) {
  const requestOptions = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password })
  };

  return fetch(Config.apiUrl + "/login", requestOptions)
    .then(handleResponse)
    .then(resp => {
      // login successful if there's a jwt token in the response
      const user = resp.user;
      if (resp.token) {
        // store user details and jwt token in local storage to keep user logged in between page refreshes
        user.token = resp.token;
        localStorage.setItem("user", JSON.stringify(user));
      }
      return user;
    });
}

function updateSubmission(submission: any) {
  return axios({
    url: `${Config.apiUrl}/updateSubmissions`,
    method: "POST",
    responseType: "json",
    headers: authHeader(),
    data: submission
  })
    .then(resp => Promise.resolve(resp.data))
    .catch(handleAxiosError);
}

function register(user: JSON) {
  const requestOptions = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(user)
  };

  return fetch(`${Config.apiUrl}/register`, requestOptions).then(
    handleResponse
  );
}

function requestPackage(packageId, reason) {
  return axios({
    url: `${Config.apiUrl}/requestPackage`,
    method: "POST",
    responseType: "json",
    headers: authHeader(),
    data: { packageId, reason }
  })
    .then(resp => Promise.resolve(resp.data))
    .catch(handleAxiosError);
}

function requestRole(role, reason) {
  return axios({
    url: `${Config.apiUrl}/requestRole`,
    method: "POST",
    responseType: "json",
    headers: authHeader(),
    data: { role, reason }
  })
    .then(resp => Promise.resolve(resp.data))
    .catch(handleAxiosError);
}

function getSubmissions() {
  return axios({
    url: `${Config.apiUrl}/submissionsForUser`,
    method: "GET",
    responseType: "json",
    headers: authHeader()
  })
    .then(resp => Promise.resolve(resp.data))
    .catch(handleAxiosError);
}

function changeSubmissionState(id, visible) {
  return axios({
    url: `${Config.apiUrl}/changeSubmissionState`,
    method: "POST",
    responseType: "json",
    headers: authHeader(),
    data: {
      id,
      visible
    }
  })
    .then(resp => Promise.resolve(resp.data))
    .catch(handleAxiosError);
}

function getBenchmark(challenge) {
  return axios({
    url: `${Config.apiUrl}/submissions/` + challenge,
    method: "GET",
    responseType: "json"
  })
    .then(resp => Promise.resolve(resp.data))
    .catch(handleAxiosError);
}

function getAdminBenchmark(challenge) {
  return axios({
    url: `${Config.apiUrl}/admin/submissions/` + challenge,
    method: "GET",
    responseType: "json",
    headers: authHeader()
  })
    .then(resp => Promise.resolve(resp.data))
    .catch(handleAxiosError);
}

function getSubmissionsForId(id) {
  return axios({
    url: `${Config.apiUrl}/submissionsById/` + id,
    method: "GET",
    responseType: "json",
    headers: authHeader()
  })
    .then(resp => Promise.resolve(resp.data))
    .catch(handleAxiosError);
}

function changePassword(OTP, password) {
  return axios({
    url: `${Config.apiUrl}/changePassword`,
    method: "POST",
    responseType: "json",
    headers: authHeader(),
    data: {
      OTP,
      password
    }
  })
    .then(resp => Promise.resolve(resp.data))
    .catch(handleAxiosError);
}

function requestNewPassword(username) {
  return axios({
    url: `${Config.apiUrl}/forgotPassword/${username}`,
    method: "GET",
    responseType: "json"
  })
    .then(resp => Promise.resolve(resp.data))
    .catch(handleAxiosError);
}

function requestUsername(email) {
  return axios({
    url: `${Config.apiUrl}/forgotUsername/${email}`,
    method: "GET",
    responseType: "json"
  })
    .then(resp => Promise.resolve(resp.data))
    .catch(handleAxiosError);
}

function checkUserAllowedToSubmit(challenge, task) {
  return axios({
    url: `${Config.apiUrl}/checkSubmissionLimit/${challenge}/${task}`,
    method: "GET",
    responseType: "json",
    headers: authHeader()
  }).then(resp => {
    if (resp.data.msg == "OK") return Promise.resolve(resp.data);
    else return Promise.reject(resp.data);
  });
}

function resendActivation(username) {
  return axios({
    url: `${Config.apiUrl}/resendActivation/${username}`,
    method: "GET",
    responseType: "json"
  }).then(resp => {
    if (resp.status == 200) return Promise.resolve(resp.data);
    else return Promise.reject(resp.data);
  });
}

// ------------------------ Admin API callbacks ----------------------------

function getPackagesForUser() {
  return axios({
    url: `${Config.apiUrl}/packages`,
    method: "GET",
    responseType: "json",
    headers: authHeader()
  })
    .then(resp => Promise.resolve(resp.data))
    .catch(handleAxiosError);
}

function activatePendingRequest(userId: string) {
  return axios({
    url: `${Config.apiUrl}/admin/requests`,
    method: "POST",
    responseType: "json",
    headers: authHeader(),
    data: { userId: userId }
  })
    .then(resp => Promise.resolve(resp.data))
    .catch(handleAxiosError);
}

function listPendingLinkActivation() {
  return axios({
    url: `${Config.apiUrl}/admin/getPendingActivations`,
    method: "GET",
    responseType: "json",
    headers: authHeader()
  })
    .then(resp => Promise.resolve(resp.data))
    .catch(handleAxiosError);
}

function listPendingRequests() {
  return axios({
    url: `${Config.apiUrl}/admin/requests`,
    method: "GET",
    responseType: "json",
    headers: authHeader()
  })
    .then(resp => Promise.resolve(resp.data))
    .catch(handleAxiosError);
}

function listPendingPackageRequests() {
  return axios({
    url: `${Config.apiUrl}/admin/permissionRequests`,
    method: "GET",
    responseType: "json",
    headers: authHeader()
  })
    .then(resp => Promise.resolve(resp.data))
    .catch(handleAxiosError);
}

function grantAccessForPackageRequest(userId, packageId) {
  console.log(userId, packageId);
  console.log("ids");
  return axios({
    url: `${Config.apiUrl}/admin/packageRequests`,
    method: "POST",
    responseType: "json",
    headers: authHeader(),
    data: { userId, packageId }
  })
    .then(resp => Promise.resolve(resp.data))
    .catch(handleAxiosError);
}

function grantAccessForRoleRequest(userId, roleId) {
  return axios({
    url: `${Config.apiUrl}/admin/roleRequests`,
    method: "POST",
    responseType: "json",
    headers: authHeader(),
    data: { userId, roleId }
  })
    .then(resp => Promise.resolve(resp.data))
    .catch(handleAxiosError);
}

function addNewPackage(newPackage) {
  const formData = new FormData();
  for (const key in newPackage) {
    formData.append(key, newPackage[key]);
  }

  return axios
    .post(`${Config.apiUrl}/admin/addPackage`, formData, {
      headers: authHeader()
    })
    .then(resp => Promise.resolve(resp.data))
    .catch(handleAxiosError);
}

function getAllPackages() {
  return axios({
    url: `${Config.apiUrl}/admin/allPackages`,
    method: "GET",
    responseType: "json",
    headers: authHeader()
  })
    .then(resp => Promise.resolve(resp.data))
    .catch(handleAxiosError);
}

function getAllRoles() {
  return axios({
    url: `${Config.apiUrl}/admin/roles`,
    method: "GET",
    responseType: "json",
    headers: authHeader()
  })
    .then(resp => Promise.resolve(resp.data))
    .catch(handleAxiosError);
}

function submitRoleChanges(newRole) {
  return axios({
    url: `${Config.apiUrl}/admin/roles`,
    method: "POST",
    responseType: "json",
    headers: authHeader(),
    data: { id: newRole._id, packages: newRole.packages }
  })
    .then(resp => Promise.resolve(resp.data))
    .catch(handleAxiosError);
}

function getUsers() {
  return axios({
    url: `${Config.apiUrl}/admin/users`,
    method: "GET",
    responseType: "json",
    headers: authHeader()
  })
    .then(resp => Promise.resolve(resp.data))
    .catch(handleAxiosError);
}

function addRoleToUser(userId, role) {
  return axios({
    url: `${Config.apiUrl}/admin/user/addRole`,
    method: "POST",
    responseType: "json",
    headers: authHeader(),
    data: { id: userId, role }
  })
    .then(resp => Promise.resolve(resp.data))
    .catch(handleAxiosError);
}

function getMD5ForPackage(packageId) {
  return axios({
    url: `${Config.apiUrl}/packageMD5/${packageId}`,
    method: "GET",
    responseType: "json",
    headers: authHeader()
  })
    .then(resp => Promise.resolve(resp.data))
    .catch(handleAxiosError);
}

function removeRoleFromUser(userId, role) {
  return axios({
    url: `${Config.apiUrl}/admin/user/removeRole`,
    method: "POST",
    responseType: "json",
    headers: authHeader(),
    data: { id: userId, role }
  })
    .then(resp => Promise.resolve(resp.data))
    .catch(handleAxiosError);
}

function agreeToContract() {
  return axios({
    url: `${Config.apiUrl}/agreeToLicense`,
    method: "GET",
    responseType: "json",
    headers: authHeader()
  })
    .then(resp => Promise.resolve(resp.data))
    .catch(handleAxiosError);
}
export const apiService = {
  login,
  logout,
  register,
  activateUser,
  listDatasets,
  listPendingRequests,
  activatePendingRequest,
  listPendingPackageRequests,
  grantAccessForPackageRequest,
  addNewPackage,
  getAllPackages,
  getPackagesForUser,
  getAllRoles,
  submitRoleChanges,
  getUsers,
  addRoleToUser,
  removeRoleFromUser,
  requestPackage,
  requestRole,
  grantAccessForRoleRequest,
  getSubmissions,
  changeSubmissionState,
  getBenchmark,
  getSubmissionsForId,
  changePassword,
  requestNewPassword,
  requestUsername,
  getAdminBenchmark,
  checkUserAllowedToSubmit,
  getMD5ForPackage,
  updateSubmission,
  resendActivation,
  listPendingLinkActivation,
  agreeToContract
};
